import { ParsedCSVData, ValidationError } from '@/types/upload';
import {
  IncentivePurchaseTypeSchema,
  IncentiveTypeSchema,
  IncentiveValueTypeSchema,
} from '@/types/api.zod';
import { type CsvRow, toCsvRow } from '@/utils/csvRow';

/**
 * インセンティブ CSV のクライアント側バリデーション。
 *
 * S3 へ presigned URL でアップロードする前に、Coupon と同様に全ルールをクライアントで検証して
 * プレビュー可否を判定する。サーバー（ProcessUpload Lambda / IncentiveUploadService）でも
 * 同一ルールを再検証する（多層防御）。
 *
 * Coupon と同じ **1 行 = 1 マスタ設定**のフラット形式（11 列）。
 *
 * 仕様の正本: docs/features/incentive.md「CSV フォーマット定義」「バリデーション」。
 * incentive_type / incentive_value_type / purchase_type の許容値は codegen の
 * Zod enum（単一の出所）から導出する。
 *
 * 権威はサーバー（incentive_service.py）側にあり、ここはアップロード前の UX 向け事前検証。
 * 日時 / purchase 専用列 / 値タイプ別検証のルールはバックエンドと手動ミラーのため、
 * 仕様変更時は docs/features/incentive.md を基点に incentive_service.py と本ファイルの
 * 両方を更新すること。
 *
 * coupon_id（incentive_value_type=coupon）/ 特集ページ slug（page）の実在確認は
 * サーバー側でも行わない。代替として値の**書式**を検証し、列の入れ違いを検出する。
 * purchase_type=group の値は日本語の作品グループ名のため書式検証しない。
 */

const COLUMNS = [
  'incentive_id',
  'title',
  'start_at',
  'end_at',
  'incentive_type',
  'purchase_type',
  'purchase_conditions',
  'incentive_value_type',
  'incentive_value',
  'exclude_coupon_used',
  'platform',
] as const;

// 列名をキーに持つ 1 行分の型（値は常に string）。詳細は utils/csvRow.ts。
type IncentiveColumn = (typeof COLUMNS)[number];
type IncentiveRow = CsvRow<typeof COLUMNS>;

// 必須列（docs/features/incentive.md 列定義の ○ 印）。
// 条件付き必須（purchase_type / purchase_conditions は purchase のみ）は個別ルールで検証する。
const REQUIRED_COLUMNS: readonly IncentiveColumn[] = [
  'incentive_id',
  'title',
  'start_at',
  'incentive_type',
  'incentive_value_type',
  'incentive_value',
  'platform',
];

// purchase のみ意味を持つ列。purchase 以外で値があれば列ズレとしてエラーにする。
const PURCHASE_ONLY_COLUMNS: readonly IncentiveColumn[] = [
  'purchase_type',
  'purchase_conditions',
  'exclude_coupon_used',
];

// 日時列（すべて JST として解釈する）。
const DATETIME_COLUMNS: readonly IncentiveColumn[] = ['start_at', 'end_at'];

// 半角英数 + `-` / `_` の 16〜64 文字（schemas の IncentiveCsvUpload と同一）。区切りに使う `#` を
// 含まないため `INCENTIVE#<incentive_id>` の DynamoDB キーへそのまま埋め込める。
const INCENTIVE_ID_RE = /^[A-Za-z0-9_-]{16,64}$/;
const JDCN_RE = /^[a-zA-Z0-9]{20}$/; // purchase_type=product の書式検証
// page（特集ページ slug）/ coupon（coupon_id）の書式。実在確認の代替ではなく列の入れ違い検出用。
const SLUG_RE = /^[a-zA-Z0-9_-]+$/;
const FLAG_RE = /^[01]$/;
// CSV platform は 3 桁の 0/1（web/ios/android の順、1=対象/0=対象外）。サーバー側の取込時に
// {web/ios/android: bool} の map へ正規化される（special / corner と同方針）。
const PLATFORM_RE = /^[01]{3}$/;
const INTEGER_RE = /^\d+$/;
// CSV 日時（`YYYY/M/D H:MM`・JST）。commons の CsvDateTime（codegen）と同じ範囲を受理する:
// 年月日と時は `/` `-` 区切り・ゼロパディング省略可だが、**分と秒は 2 桁固定**。
// 時刻の省略とタイムゾーン表記は認めない。
// ⚠ incentive_service.py の _CSV_DATETIME_PATTERN と完全に一致させること。緩いとサーバーだけが
// 弾く形になり（例 `2026/9/1 0:0`）、事前検証を通ったのに全行エラーになる。
const CSV_DATETIME_RE = /^(\d{4})[/-](\d{1,2})[/-](\d{1,2}) (\d{1,2}):(\d{2})(?::(\d{2}))?$/;
const DATETIME_FORMAT_HINT = '（例: 2026/9/1 0:00・JST。日付のみ / タイムゾーン付きは不可）';

const PERCENT_VALUE_MIN = 1;
const PERCENT_VALUE_MAX = 100; // incentive_value_type=percent は還元率 %
const POINT_VALUE_MIN = 1; // incentive_value_type=point の下限

const INCENTIVE_TYPES: readonly string[] = IncentiveTypeSchema.options;
const VALUE_TYPES: readonly string[] = IncentiveValueTypeSchema.options;
const PURCHASE_TYPES: readonly string[] = IncentivePurchaseTypeSchema.options;

const err = (row: number, column: string, value: string, error: string): ValidationError => ({
  row,
  column,
  value,
  error,
});

// 存在しない日付（例: 2026-02-30 / 2026-06-31）を UTC ロールオーバーの有無で弾く
const isRealDate = (year: number, month: number, day: number): boolean => {
  const probe = new Date(Date.UTC(year, month - 1, day));
  return (
    probe.getUTCFullYear() === year &&
    probe.getUTCMonth() === month - 1 &&
    probe.getUTCDate() === day
  );
};

type DateTimeParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

/**
 * CSV 日時を分解する。書式不正・時刻範囲外・存在しない日付は null。
 * 形式判定と epoch 変換の両方をこの 1 箇所に集約している（couponCsvValidation と同一方式）。
 */
const parseDateTime = (value: string): DateTimeParts | null => {
  const matched = CSV_DATETIME_RE.exec(value.trim());
  if (!matched) return null;
  const [, y, mo, d, h, mi, sec] = matched;
  const parts: DateTimeParts = {
    year: Number(y),
    month: Number(mo),
    day: Number(d),
    hour: Number(h),
    minute: Number(mi),
    second: Number(sec ?? 0),
  };
  if (parts.hour > 23 || parts.minute > 59 || parts.second > 59) return null;
  if (!isRealDate(parts.year, parts.month, parts.day)) return null;
  return parts;
};

const isValidDateTime = (value: string): boolean => parseDateTime(value) !== null;

// CSV のタイムゾーン無し日時は JST（UTC+9）とみなす（サーバ DateTimeUtils と一致）。
const JST_OFFSET_MS = 9 * 60 * 60 * 1000;

// 大小関係の判定用。壁時計時刻を Date.UTC で組み立てて JST オフセットを引く。
const toEpoch = (p: DateTimeParts): number =>
  Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - JST_OFFSET_MS;

const validateRequired = (rowNum: number, d: IncentiveRow, errors: ValidationError[]): void => {
  REQUIRED_COLUMNS.forEach((col) => {
    if (!d[col]) errors.push(err(rowNum, col, d[col], `${col} は必須です`));
  });
};

const validateEnums = (rowNum: number, d: IncentiveRow, errors: ValidationError[]): void => {
  if (d.incentive_type && !INCENTIVE_TYPES.includes(d.incentive_type)) {
    errors.push(
      err(
        rowNum,
        'incentive_type',
        d.incentive_type,
        `incentive_type は ${INCENTIVE_TYPES.join(' / ')} のいずれかです`
      )
    );
  }
  if (d.incentive_value_type && !VALUE_TYPES.includes(d.incentive_value_type)) {
    errors.push(
      err(
        rowNum,
        'incentive_value_type',
        d.incentive_value_type,
        `incentive_value_type は ${VALUE_TYPES.join(' / ')} のいずれかです`
      )
    );
  }
};

const validateDateTimes = (rowNum: number, d: IncentiveRow, errors: ValidationError[]): void => {
  DATETIME_COLUMNS.forEach((col) => {
    if (d[col] && !isValidDateTime(d[col])) {
      errors.push(err(rowNum, col, d[col], `${col} の日時形式が不正です${DATETIME_FORMAT_HINT}`));
    }
  });
};

/** start_at < end_at を検証する（両端の形式が正しいときだけ。end_at 空欄は無期限として有効） */
const validatePeriodOrder = (rowNum: number, d: IncentiveRow, errors: ValidationError[]): void => {
  const start = parseDateTime(d.start_at);
  const end = parseDateTime(d.end_at);
  if (start === null || end === null) return; // 空欄・形式不正はここでは扱わない
  if (toEpoch(start) >= toEpoch(end)) {
    errors.push(
      err(rowNum, 'end_at', d.end_at, 'start_at は end_at より前の日時で指定してください')
    );
  }
};

/** purchase 専用列（purchase_type / purchase_conditions / exclude_coupon_used）の整合 */
const validatePurchaseColumns = (
  rowNum: number,
  d: IncentiveRow,
  errors: ValidationError[]
): void => {
  if (d.incentive_type === 'purchase') {
    // purchase では対象の指定方法と対象リストが必須（判定 Lambda が対象解決に使う）
    if (!d.purchase_type) {
      errors.push(
        err(rowNum, 'purchase_type', '', 'incentive_type=purchase の場合 purchase_type は必須です')
      );
    } else if (!PURCHASE_TYPES.includes(d.purchase_type)) {
      errors.push(
        err(
          rowNum,
          'purchase_type',
          d.purchase_type,
          `purchase_type は ${PURCHASE_TYPES.join(' / ')} のいずれかです`
        )
      );
    }

    const values = d.purchase_conditions
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v !== '');
    if (values.length === 0) {
      errors.push(
        err(
          rowNum,
          'purchase_conditions',
          d.purchase_conditions,
          'incentive_type=purchase の場合 purchase_conditions は必須です'
        )
      );
    } else {
      // 同一値の重複は意図しない指定の可能性が高いため明示的にエラーにする（Coupon の use_list と同じ）。
      const duplicated = [
        ...new Set(values.filter((v) => values.indexOf(v) !== values.lastIndexOf(v))),
      ];
      if (duplicated.length > 0) {
        errors.push(
          err(
            rowNum,
            'purchase_conditions',
            d.purchase_conditions,
            `purchase_conditions に重複した値があります: ${duplicated.join(' / ')}`
          )
        );
      }
      // 書式検証（列の入れ違い検出用。実在確認は行わない）。
      // product は JDCN（半角英数 20 桁）。group は日本語の作品グループ名のため書式は検証しない。
      if (d.purchase_type === 'product') {
        const invalid = values.filter((v) => !JDCN_RE.test(v));
        if (invalid.length > 0) {
          errors.push(
            err(
              rowNum,
              'purchase_conditions',
              d.purchase_conditions,
              `purchase_type=product の purchase_conditions は JDCN（半角英数 20 桁）です: ${invalid.join(' / ')}`
            )
          );
        }
      }
    }

    if (d.exclude_coupon_used && !FLAG_RE.test(d.exclude_coupon_used)) {
      errors.push(
        err(
          rowNum,
          'exclude_coupon_used',
          d.exclude_coupon_used,
          'exclude_coupon_used は 0 / 1 で指定してください'
        )
      );
    }
  } else if (d.incentive_type) {
    // purchase 以外で purchase 専用列に値があれば列ズレとしてエラーにする
    const filled = PURCHASE_ONLY_COLUMNS.filter((col) => d[col]);
    const [firstFilled] = filled;
    if (firstFilled !== undefined) {
      errors.push(
        err(
          rowNum,
          firstFilled,
          d[firstFilled],
          `${filled.join(' / ')} は incentive_type=purchase の場合のみ指定できます（incentive_type=${d.incentive_type}）`
        )
      );
    }
  }
};

/**
 * incentive_type × incentive_value_type の組合せの検証（#1092）
 *
 * | incentive_type | point | percent |
 * |---|---|---|
 * | purchase | 不可 | 可 |
 * | regist / login | 可 | 不可 |
 *
 * incentive_value の内容は見ない。組合せ自体の成否は特典値と無関係で、値の形式エラーと
 * 同時に出さないと「値を直して再アップロードしてから初めて気づく」という二度手間になるため、
 * validateIncentiveValue とは独立して判定する（サーバー側 `_validate_value_type_combination` と同じ）。
 */
const validateValueTypeCombination = (
  rowNum: number,
  d: IncentiveRow,
  errors: ValidationError[]
): void => {
  const valueType = d.incentive_value_type;
  const incentiveType = d.incentive_type;
  if (!valueType || !incentiveType) return; // 必須・許容値チェックは別で行う

  // purchase の point は割引後の残額より多いポイントを返しうる（支払額を超えるポイントバックに
  // なる）ため禁止する。購入に対する還元は percent（購入金額基準）で表現する。
  if (valueType === 'point' && incentiveType === 'purchase') {
    errors.push(
      err(
        rowNum,
        'incentive_value_type',
        valueType,
        'incentive_value_type=point は incentive_type=purchase の場合は指定できません'
      )
    );
  }
  // percent は購入金額を基準に計算するため、purchase 以外では付与量が決まらない
  // （docs/features/incentive.md バリデーション「エラー（採用）」）。
  if (valueType === 'percent' && incentiveType !== 'purchase') {
    errors.push(
      err(
        rowNum,
        'incentive_value_type',
        valueType,
        'incentive_value_type=percent は incentive_type=purchase の場合のみ指定できます'
      )
    );
  }
};

/**
 * incentive_value_type に応じた incentive_value の検証
 *
 * incentive_type との組合せは validateValueTypeCombination が見る（ここでは扱わない）。
 */
const validateIncentiveValue = (
  rowNum: number,
  d: IncentiveRow,
  errors: ValidationError[]
): void => {
  const valueType = d.incentive_value_type;
  const value = d.incentive_value;
  if (!value) return; // 必須チェックは validateRequired に集約

  if (valueType === 'point' || valueType === 'percent') {
    if (!INTEGER_RE.test(value)) {
      errors.push(
        err(
          rowNum,
          'incentive_value',
          value,
          `incentive_value_type=${valueType} の incentive_value は整数で指定してください`
        )
      );
      return;
    }
    const number = Number(value);
    if (valueType === 'point' && number < POINT_VALUE_MIN) {
      errors.push(
        err(
          rowNum,
          'incentive_value',
          value,
          `incentive_value_type=point の incentive_value は ${POINT_VALUE_MIN} 以上で指定してください`
        )
      );
    }
    if (valueType === 'percent' && (number < PERCENT_VALUE_MIN || number > PERCENT_VALUE_MAX)) {
      errors.push(
        err(
          rowNum,
          'incentive_value',
          value,
          `incentive_value_type=percent の incentive_value は ${PERCENT_VALUE_MIN}〜${PERCENT_VALUE_MAX} で指定してください`
        )
      );
    }
  } else if ((valueType === 'page' || valueType === 'coupon') && !SLUG_RE.test(value)) {
    // 実在確認は行わない（サーバー側と同じ判断）。書式のみを検証する。
    const label = valueType === 'page' ? '特集ページの slug' : 'coupon_id';
    errors.push(
      err(
        rowNum,
        'incentive_value',
        value,
        `incentive_value_type=${valueType} の incentive_value（${label}）は半角英数・ハイフン（-）・アンダースコア（_）のみ使用できます`
      )
    );
  }
};

export const validateIncentiveCsv = (data: ParsedCSVData): ValidationError[] => {
  const errors: ValidationError[] = [];

  // ヘッダー（順序込み完全一致）
  const headerMatches =
    data.headers.length === COLUMNS.length && COLUMNS.every((c, i) => data.headers[i] === c);
  if (!headerMatches) {
    return [
      err(
        1,
        'ヘッダー',
        data.headers.join(','),
        `CSVヘッダーが不正です。期待: ${COLUMNS.join(',')}`
      ),
    ];
  }

  if (data.rows.length === 0) {
    return [
      err(1, 'データ', '', 'データ行がありません（1=カラム名 / 2=定義名 / 3 行目以降=データ）'),
    ];
  }

  // parseCSV が 2 行目（定義名）をスキップ済みのため data.rows[0] は実ファイル 3 行目に相当する。
  const seenIncentiveIds = new Map<string, number>();

  data.rows.forEach((row, idx) => {
    const rowNum = idx + 3;

    if (row.length !== COLUMNS.length) {
      errors.push(
        err(
          rowNum,
          '列数',
          row.join(','),
          `列数が不正です（${row.length} 列、期待 ${COLUMNS.length} 列）`
        )
      );
      return;
    }

    const d = toCsvRow(COLUMNS, row);

    validateRequired(rowNum, d, errors);
    if (d.incentive_id && !INCENTIVE_ID_RE.test(d.incentive_id)) {
      errors.push(
        err(
          rowNum,
          'incentive_id',
          d.incentive_id,
          'incentive_id は半角英数・ハイフン（-）・アンダースコア（_）の 16〜64 文字で指定してください'
        )
      );
    }
    validateDateTimes(rowNum, d, errors);
    validateEnums(rowNum, d, errors);
    validatePeriodOrder(rowNum, d, errors);
    validatePurchaseColumns(rowNum, d, errors);
    validateValueTypeCombination(rowNum, d, errors);
    validateIncentiveValue(rowNum, d, errors);
    if (d.platform && !PLATFORM_RE.test(d.platform)) {
      errors.push(
        err(
          rowNum,
          'platform',
          d.platform,
          'platform は 3 桁の 0/1 で指定してください（web/ios/android の順。例: 111）'
        )
      );
    }

    // incentive_id の重複（後の行が前の行を上書きするため追跡不能になる）
    if (d.incentive_id) {
      const firstRow = seenIncentiveIds.get(d.incentive_id);
      if (firstRow !== undefined) {
        errors.push(
          err(
            rowNum,
            'incentive_id',
            d.incentive_id,
            `incentive_id が重複しています: '${d.incentive_id}'（${firstRow} 行目と同一）`
          )
        );
      } else {
        seenIncentiveIds.set(d.incentive_id, rowNum);
      }
    }
  });

  return errors;
};
